import React, { useState } from 'react';
import { usePayerWizardContext } from '../PayerWizardContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, ArrowRight, Zap, Globe, Phone, AlertTriangle, ChevronUp, ChevronDown, Check, Shield } from 'lucide-react';
import { LANE_DISPLAY, validateLaneConfig, recalculateLanePriority } from '@/lib/payer-wizard/types';
import type { LaneType, PlanLaneConfig } from '@/lib/payer-wizard/types';
import { SetupCompletionChecklist } from '../SetupCompletionChecklist';
import { generateChecklist } from '@/lib/payer-wizard/checklistUtils';

type LaneStep = 'toggle' | 'priority' | 'optional';

export function PayerStep4LaneConfig() {
  const { 
    progress, 
    ruleReview,
    validateStep4, 
    completeStep, 
    goToStep,
    updatePlanLaneConfig,
  } = usePayerWizardContext();
  
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const [laneStep, setLaneStep] = useState<LaneStep>('toggle');
  
  if (!progress) return null;
  
  const validation = validateStep4();
  const currentPlan = progress.plans[activePlanIndex];
  const currentLaneConfig = progress.plan_lane_configs[activePlanIndex];
  const checklist = generateChecklist(progress, ruleReview);
  
  if (!currentPlan || !currentLaneConfig) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">No plans configured. Go back to Step 2.</p>
          <div className="flex justify-center pt-4">
            <Button variant="outline" onClick={() => goToStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Plans
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const handleToggleLane = (lane: LaneType, enabled: boolean) => {
    const newConfig: PlanLaneConfig = {
      ...currentLaneConfig,
      lanes_enabled: {
        ...currentLaneConfig.lanes_enabled,
        [lane]: enabled,
      },
    };
    // Recalculate priority when toggling
    newConfig.lane_priority = recalculateLanePriority(newConfig);
    updatePlanLaneConfig(activePlanIndex, newConfig);
  };
  
  const handleMoveLane = (lane: LaneType, direction: 'up' | 'down') => {
    const priority = [...currentLaneConfig.lane_priority];
    const idx = priority.indexOf(lane);
    if (idx === -1) return;
    
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= priority.length) return;
    
    [priority[idx], priority[newIdx]] = [priority[newIdx], priority[idx]];
    
    updatePlanLaneConfig(activePlanIndex, {
      ...currentLaneConfig,
      lane_priority: priority,
    });
  };
  
  const handleOptionalFieldChange = (field: 'portal_url' | 'phone' | 'fax', value: string) => {
    updatePlanLaneConfig(activePlanIndex, {
      ...currentLaneConfig,
      [field]: value || null,
    });
  };
  
  // Apply safe defaults: PORTAL + FAX_PHONE enabled, PAS off, priority set
  const handleApplySafeDefaults = () => {
    const safeConfig: PlanLaneConfig = {
      ...currentLaneConfig,
      lanes_enabled: {
        FHIR_PAS: false,
        PORTAL: true,
        FAX_PHONE: true,
      },
      lane_priority: ['PORTAL', 'FAX_PHONE'],
    };
    updatePlanLaneConfig(activePlanIndex, safeConfig);
  };
  
  const laneValidation = validateLaneConfig(currentLaneConfig);
  
  const handleContinue = () => {
    if (validation.isValid) {
      completeStep(4);
    }
  };

  const getLaneIcon = (lane: LaneType) => {
    switch (lane) {
      case 'FHIR_PAS': return <Zap className="h-5 w-5 text-primary" />;
      case 'PORTAL': return <Globe className="h-5 w-5 text-blue-500" />;
      case 'FAX_PHONE': return <Phone className="h-5 w-5 text-amber-500" />;
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Step 4: Lane Configuration
        </CardTitle>
        <CardDescription>
          Configure submission lanes for each plan. Default: PORTAL and FAX_PHONE enabled.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Completion Checklist */}
        <SetupCompletionChecklist items={checklist.items} compact />
        
        {/* Safe Defaults Button */}
        <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/20">
          <div>
            <p className="font-medium text-sm">Quick Setup</p>
            <p className="text-xs text-muted-foreground">Apply recommended lane configuration</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleApplySafeDefaults}>
            <Shield className="mr-2 h-4 w-4" />
            Apply Safe Defaults
          </Button>
        </div>
        
        {/* Plan Selector (if multiple plans) */}
        {progress.plans.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {progress.plans.map((plan, idx) => {
              const planConfig = progress.plan_lane_configs[idx];
              const planValid = planConfig ? validateLaneConfig(planConfig).valid : false;
              return (
                <Button
                  key={idx}
                  variant={activePlanIndex === idx ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActivePlanIndex(idx)}
                  className="relative"
                >
                  {plan.plan_name}
                  {planValid && (
                    <Check className="ml-1 h-3 w-3 text-green-500" />
                  )}
                </Button>
              );
            })}
          </div>
        )}
        
        <div className="border rounded-lg p-4">
          <h3 className="font-medium mb-4">
            Configuring: <span className="text-primary">{currentPlan.plan_name}</span>
          </h3>
          
          <Tabs value={laneStep} onValueChange={(v) => setLaneStep(v as LaneStep)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="toggle">A. Toggle Lanes</TabsTrigger>
              <TabsTrigger value="priority">B. Set Priority</TabsTrigger>
              <TabsTrigger value="optional">C. Optional Details</TabsTrigger>
            </TabsList>
            
            {/* Step A: Toggle Lanes */}
            <TabsContent value="toggle" className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                Enable or disable submission lanes. At least one must be enabled.
              </p>
              
              {/* FHIR PAS */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Zap className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <Label className="font-medium">{LANE_DISPLAY.FHIR_PAS.label}</Label>
                    <p className="text-sm text-muted-foreground">{LANE_DISPLAY.FHIR_PAS.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {currentLaneConfig.lanes_enabled.FHIR_PAS && (
                    <Badge variant="default" className="bg-primary">Enabled</Badge>
                  )}
                  <Switch
                    checked={currentLaneConfig.lanes_enabled.FHIR_PAS}
                    onCheckedChange={(checked) => handleToggleLane('FHIR_PAS', checked)}
                  />
                </div>
              </div>
              
              {/* Portal */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Globe className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <Label className="font-medium">{LANE_DISPLAY.PORTAL.label}</Label>
                    <p className="text-sm text-muted-foreground">{LANE_DISPLAY.PORTAL.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {currentLaneConfig.lanes_enabled.PORTAL && (
                    <Badge variant="default" className="bg-blue-500">Enabled</Badge>
                  )}
                  <Switch
                    checked={currentLaneConfig.lanes_enabled.PORTAL}
                    onCheckedChange={(checked) => handleToggleLane('PORTAL', checked)}
                  />
                </div>
              </div>
              
              {/* Fax/Phone */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <Phone className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <Label className="font-medium">{LANE_DISPLAY.FAX_PHONE.label}</Label>
                    <p className="text-sm text-muted-foreground">{LANE_DISPLAY.FAX_PHONE.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {currentLaneConfig.lanes_enabled.FAX_PHONE && (
                    <Badge variant="default" className="bg-amber-500">Enabled</Badge>
                  )}
                  <Switch
                    checked={currentLaneConfig.lanes_enabled.FAX_PHONE}
                    onCheckedChange={(checked) => handleToggleLane('FAX_PHONE', checked)}
                  />
                </div>
              </div>
              
              {!laneValidation.valid && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{laneValidation.error}</AlertDescription>
                </Alert>
              )}
            </TabsContent>
            
            {/* Step B: Set Priority */}
            <TabsContent value="priority" className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                Order enabled lanes by priority. First lane will be tried first.
              </p>
              
              {currentLaneConfig.lane_priority.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No lanes enabled. Enable lanes in Step A.</p>
              ) : (
                <div className="space-y-2">
                  {currentLaneConfig.lane_priority.map((lane, idx) => (
                    <div key={lane} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-muted-foreground w-6">{idx + 1}.</span>
                        {getLaneIcon(lane)}
                        <span className="font-medium">{LANE_DISPLAY[lane].label}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={idx === 0}
                          onClick={() => handleMoveLane(lane, 'up')}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={idx === currentLaneConfig.lane_priority.length - 1}
                          onClick={() => handleMoveLane(lane, 'down')}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Step C: Optional Details */}
            <TabsContent value="optional" className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                Optional contact details for this plan. You can skip and add later.
              </p>
              
              {currentLaneConfig.lanes_enabled.PORTAL && (
                <div className="space-y-2">
                  <Label htmlFor="plan_portal_url">Portal URL (Optional)</Label>
                  <Input
                    id="plan_portal_url"
                    type="url"
                    value={currentLaneConfig.portal_url || progress.portal_url || ''}
                    onChange={(e) => handleOptionalFieldChange('portal_url', e.target.value)}
                    placeholder="https://provider.payer.com"
                  />
                </div>
              )}
              
              {currentLaneConfig.lanes_enabled.FAX_PHONE && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="plan_phone">Phone (Optional)</Label>
                    <Input
                      id="plan_phone"
                      type="tel"
                      value={currentLaneConfig.phone || progress.phone || ''}
                      onChange={(e) => handleOptionalFieldChange('phone', e.target.value)}
                      placeholder="1-800-555-0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="plan_fax">Fax (Optional)</Label>
                    <Input
                      id="plan_fax"
                      type="tel"
                      value={currentLaneConfig.fax || progress.fax || ''}
                      onChange={(e) => handleOptionalFieldChange('fax', e.target.value)}
                      placeholder="1-800-555-0001"
                    />
                  </div>
                </div>
              )}
              
              <div className="pt-2">
                <Button variant="ghost" size="sm" onClick={() => setLaneStep('toggle')}>
                  ← Back to Toggle
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Overall Validation */}
        {!validation.isValid && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside">
                {validation.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => goToStep(3)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button onClick={handleContinue} disabled={!validation.isValid}>
            Continue to Activation
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
